/* eslint-disable no-underscore-dangle */
import React from "react";
import { Provider } from "react-redux";
import { persistStore } from "redux-persist";

import "@template/styles/fonts.css";
import "@template/styles/global.css";
import "@template/components/Breadcrumbs/index.css";

import clientConfig from "@template/clientConfig";
import configureStore from "@template/state";
import contentTargeting from "@src/helpers/contentTargetingEvents";
import eventBus from "@src/helpers/events";
import HtmlHeader from "@template/components/HtmlHeader";
import FooterComponent from "@template/components/Footer";
import SecuredTouch from "@template/components/Footer/SecuredTouch";
import InfoBannerComponent from "@template/components/InfoBanner/InfoBannerControl";
import loadTokenBlock from "@src/helpers/loadTokenBlock";
import { dispatchCookiesPolicy } from "@src/helpers/cookiesPolicy";
import WelcomeMessageComponent from "@template/components/WelcomeMessage";
import { openCountrySelector } from "@template/state/modules/countrySelector";
import {
  LARGE_VIEWPORT,
  MEDIUM_VIEWPORT
} from "@template/state/modules/layout";

import {
  siteChromeInitialStore,
  siteChromeTokenValues,
  asosGlobalNamespace,
  siteChromeGlobalNamespace,
  sdkGlobalNamespace,
  manifestWindowKey
} from "@template/constants";

import addFeaturesToStore from "./addFeaturesToStore";
import context from "./context";
import { reloadNavigation } from "./navigation";
import { initialise as initialiseSdk } from "./sdk";
import loadUserProfile from "./loadUserProfile";
import siteChromeAnalytics from "./analytics/index";

/* <TODO>: remove once "asos-web-version-manifests" is updated to use window.asos._versionManifest (or otherwise) */
window[asosGlobalNamespace] = {
  ...window[asosGlobalNamespace],
  [manifestWindowKey]: window.__versionManifest
};
/* </TODO> */

const store = configureStore(
  window[asosGlobalNamespace][siteChromeGlobalNamespace][siteChromeInitialStore]
);
const { context: webContext, updateRegionalStore } = context(store);

window[asosGlobalNamespace] = {
  ...window[asosGlobalNamespace],
  webContext,
  contentTargeting,
  eventBus,
  [sdkGlobalNamespace]: {}
};

window.__sdk =
  window[asosGlobalNamespace][sdkGlobalNamespace]; /* backwards compatibility */
window.analyticsEventEmitter =
  window[asosGlobalNamespace].eventBus; /* backwards compatibility */
window.__siteChrome = {
  context: window[asosGlobalNamespace].webContext
}; /* backwards compatibility */

window[asosGlobalNamespace][
  siteChromeGlobalNamespace
].openCountrySelector = openCountrySelector().bind(
  null,
  store.dispatch,
  store.getState
);

const updateFeatures = addFeaturesToStore(store, eventBus);

eventBus.addListener("features-sdk-ready", () =>
  updateFeatures("features-sdk-ready")
);
eventBus.addListener("features-mvt-ready", () =>
  updateFeatures("features-mvt-ready")
);

const { NAVIGATION_API_PATH } = clientConfig.get();
export const updateStateWithClientData = () => {
  updateRegionalStore(() => {
    if (!NAVIGATION_API_PATH.includes("/preview/")) {
      reloadNavigation(store);
    }
  });
  loadUserProfile(store);
  siteChromeAnalytics();

  const siteChromeNamespace =
    window[asosGlobalNamespace][siteChromeGlobalNamespace];
  loadTokenBlock({
    store,
    initialState: siteChromeNamespace[siteChromeInitialStore],
    newState: siteChromeNamespace[siteChromeTokenValues]
  });

  dispatchCookiesPolicy(store);

  delete siteChromeNamespace[siteChromeInitialStore];
  initialiseSdk(store, webContext);

  persistStore(store, {
    keyPrefix: "chrome:",
    whitelist: ["search"]
  });
};

export const Header = () => (
  <Provider store={store}>
    <HtmlHeader />
  </Provider>
);

export const Footer = () => (
  <Provider store={store}>
    <React.Fragment>
      <FooterComponent viewable={[LARGE_VIEWPORT, MEDIUM_VIEWPORT]} />
      <SecuredTouch />
    </React.Fragment>
  </Provider>
);

export const InfoBanner = () => (
  <Provider store={store}>
    <InfoBannerComponent />
  </Provider>
);

export const WelcomeMessage =
  /* istanbul ignore next */
  () => (
    <Provider store={store}>
      <WelcomeMessageComponent />
    </Provider>
  );
